/**
 * Copying to the clipboard, and saying what happened when it could not be done.
 *
 * Copy is the primary action in three places — the consent link on the Awaiting
 * consent tab, the same link in a row's reconnect drawer, and the block of links
 * a bulk import produces — so it is one implementation with one set of failure
 * wording rather than three that would disagree about what a blocked clipboard
 * means.
 */

type CopyOutcome = 'copied' | 'selected' | 'failed';

const selectNode = (node: HTMLElement | null): boolean => {
  if (!node) return false;

  if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
    node.focus();
    node.select();
    return true;
  }

  const selection = window.getSelection();
  if (!selection) return false;

  const range = document.createRange();
  range.selectNodeContents(node);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

/**
 * `navigator.clipboard` is absent outside a secure context and the write can be
 * refused even where it exists. Both endings have to leave the user somewhere
 * useful, so the fallback selects the text and says so — a copy button that
 * quietly does nothing is worse than no copy button.
 */
export const copy = async (text: string, fallback: HTMLElement | null): Promise<CopyOutcome> => {
  if (navigator.clipboard?.writeText) {
    const written = await navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
    if (written) return 'copied';
  }

  return selectNode(fallback) ? 'selected' : 'failed';
};

export const copyOutcomeText = (outcome: CopyOutcome, what: string): string => {
  if (outcome === 'copied') return `${what} copied to the clipboard.`;
  if (outcome === 'selected') {
    return `${what} selected — press Ctrl+C, or Cmd+C on a Mac, to copy it. This browser only allows automatic copying over a secure connection.`;
  }
  return `${what} could not be copied. Select the text and copy it manually.`;
};
